import React from 'react';
import { type BaseMultiPageModel, type ControllableElement } from '@bynder-studio/render-core';
import { Dropdown, Flex, List, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconCollection, IconDelete } from '@bynder/icons';
import generateTestId from 'packages/helpers/testIdHelpers';
import { AssetButton, AssetThumbnail, FakeAssetOption, RemoveButton } from './AssetSource.styled';
import { useAssetSourceMultiple } from './useAssetSourceMultiple';
import MaybeTooltip from 'packages/common/MaybeTooltip';

const testId = generateTestId('shots_asset_source_multiple');
const testIdRemove = generateTestId('shots_asset_source_multiple__remove');

interface AssetSourceMultipleProps {
    creativeModel: BaseMultiPageModel;
    selectedElements: ControllableElement[];
}

const AssetSourceMultiple = ({ creativeModel, selectedElements }: AssetSourceMultipleProps) => {
    const { translate } = useTranslate();
    const {
        containerRef,
        locked,
        isLoading,
        collections,
        isCollectionInvalid,
        isMixedState,
        chosenAsset,
        searchTerm,
        setIsOpen,
        setSearchTerm,
        handleRemoveClick,
        handleClick,
        supportedElementsCount,
        uniqueCollectionsCount,
        activeCollectionIds,
    } = useAssetSourceMultiple(creativeModel, selectedElements);

    const unlockedElements = selectedElements.filter((el) => !el.locked);

    if (supportedElementsCount === 0) {
        return null;
    }

    const getButtonText = () => {
        if (isCollectionInvalid) {
            return translate('editor.sidebar.shots.asset_source.not_found');
        }

        if (isMixedState && uniqueCollectionsCount > 0) {
            return translate('editor.sidebar.shots.asset_source.collections_in_use', { count: uniqueCollectionsCount });
        }

        if (chosenAsset) {
            return chosenAsset.name;
        }

        return 'All assets';
    };

    const getSubtext = () => {
        if (isMixedState) {
            return `${unlockedElements.length} element${unlockedElements.length > 1 ? 's' : ''}`;
        }

        if (chosenAsset && !isCollectionInvalid) {
            return `${chosenAsset.assetCount} asset${chosenAsset?.assetCount > 1 ? 's' : ''}`;
        }

        return '';
    };

    const showRemoveButton = chosenAsset || isCollectionInvalid || isMixedState;

    return (
        <List.Group title={translate('editor.sidebar.shots.asset_source.label')}>
            <Dropdown
                containerRef={containerRef}
                position="bottom"
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Flex alignItems="center" justifyContent="space-between">
                        <AssetButton
                            aria-label="All assets"
                            isSelected={isOpen}
                            isDisabled={locked}
                            rightElements={
                                <MaybeTooltip
                                    hasTooltip={isMixedState}
                                    position="top-left"
                                    content={translate('editor.sidebar.shots.asset_source.mixed_tooltip_update')}
                                    {...generateTestId('shots_asset_source_button_tooltip')}
                                    {...{ id: 'shots_asset_source_button_tooltip' }}
                                >
                                    <Dropdown.Arrow />
                                </MaybeTooltip>
                            }
                            thumbnail={
                                chosenAsset && !isMixedState ? (
                                    <AssetThumbnail
                                        size="m"
                                        // @ts-ignore
                                        variant="content"
                                        imageUrl={chosenAsset?.thumbnailUrls?.[0]}
                                    />
                                ) : (
                                    // @ts-ignore
                                    <AssetThumbnail variant="clean" size="m" icon={<IconCollection />} />
                                )
                            }
                            subtext={getSubtext()}
                            {...triggerProps}
                            {...testId}
                        >
                            {getButtonText()}
                        </AssetButton>
                        {showRemoveButton && (
                            <MaybeTooltip
                                hasTooltip
                                content={translate(
                                    isMixedState
                                        ? 'editor.sidebar.shots.asset_source.mixed_tooltip_remove'
                                        : 'editor.sidebar.shots.asset_source.remove_collection',
                                )}
                                position="top-left"
                                {...generateTestId('shots_asset_source_remove_button_tooltip')}
                                {...{ id: 'shots_asset_source_remove_button_tooltip' }}
                            >
                                <RemoveButton
                                    isDisabled={locked}
                                    onClick={handleRemoveClick}
                                    title={translate('editor.sidebar.shots.asset_source.remove_collection')}
                                    variant="clean"
                                    icon={<IconDelete />}
                                    {...testIdRemove}
                                />
                            </MaybeTooltip>
                        )}
                    </Flex>
                )}
            >
                <Dropdown.SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search"
                    aria-label="Search for collections"
                />
                {isMixedState && (
                    <>
                        <Dropdown.Text>{translate('editor.sidebar.shots.asset_source.override_hint')}</Dropdown.Text>
                        <Dropdown.Divider />
                    </>
                )}
                {collections?.map((item) => (
                    <Dropdown.ListItem
                        key={item.id}
                        aria-label={item.name}
                        isChecked={!isMixedState && item.id === chosenAsset?.id}
                        role="menuitemradio"
                        thumbnail={
                            <AssetThumbnail
                                // @ts-ignore
                                variant="content"
                                size="m"
                                imageUrl={item.thumbnailUrls[0]}
                            >
                                {isMixedState && activeCollectionIds.includes(item.id) && (
                                    <Thumbnail.Status type="offline" />
                                )}
                            </AssetThumbnail>
                        }
                        subtext={`${item.assetCount} asset${item.assetCount > 1 ? 's' : ''}`}
                        onClick={() => handleClick(item)}
                    >
                        {item.name}
                    </Dropdown.ListItem>
                ))}
                {isLoading && <FakeAssetOption>Loading...</FakeAssetOption>}
            </Dropdown>
        </List.Group>
    );
};

export default AssetSourceMultiple;
